import {
  DEFAULT_MEMBER_FEATURE_FLAGS,
  MemberFeatureFlags,
  DEFAULT_SST_FEATURE_FLAGS,
  SSTFeatureFlags,
  DEFAULT_AUTH_FEATURE_FLAGS,
  AuthFeatureFlags,
  DEFAULT_FORUMS_COMMENT_EDIT_WINDOW_HOURS,
  DEFAULT_FORUMS_COMMENT_MAX_LENGTH,
} from '@pya-mms/common';
import { DefaultCommunity } from '../constants';

export const SET_COMMUNITY_NAME = 'SET_COMMUNITY_NAME';
export const SET_COMMUNITY_LOGO = 'SET_COMMUNITY_LOGO';
export const SET_COMMUNITY_LOGO_PUBLIC_ID = 'SET_COMMUNITY_LOGO_PUBLIC_ID';
export const SET_PRIMARY_COLOR = 'SET_PRIMARY_COLOR';
export const SET_FACEBOOK_LOGIN_ENABLED = 'SET_FACEBOOK_LOGIN_ENABLED';
export const SET_GOOGLE_LOGIN_ENABLED = 'SET_GOOGLE_LOGIN_ENABLED';
export const SET_GITHUB_LOGIN_ENABLED = 'SET_GITHUB_LOGIN_ENABLED';
export const SET_MEMBER_FEATURE_FLAGS = 'SET_MEMBER_FEATURE_FLAGS';
export const SET_SST_FEATURE_FLAGS = 'SET_SST_FEATURE_FLAGS';
export const SET_AUTH_FEATURE_FLAGS = 'SET_AUTH_FEATURE_FLAGS';
export const SET_SKIP_LOGIN_2FA_ALLOWED = 'SET_SKIP_LOGIN_2FA_ALLOWED';
export const SET_FORUMS_AUDIENCE = 'SET_FORUMS_AUDIENCE';
export const SET_FORUMS_LIMITS = 'SET_FORUMS_LIMITS';

export interface Settings {
  communityName: string;
  communityLogo: string;
  communityLogoPublicId?: string;
  primaryColor: string;
  facebookLoginEnabled: boolean;
  googleLoginEnabled: boolean;
  githubLoginEnabled: boolean;
  memberFeatureFlags: MemberFeatureFlags;
  sstFeatureFlags: SSTFeatureFlags;
  authFeatureFlags: AuthFeatureFlags;
  skipLogin2FAAllowed: boolean;
  forumsAudienceTypeSlugs: string[];
  forumsCommentMaxLength: number;
  forumsCommentEditWindowHours: number;
}

export interface setCommunityNameActionType {
  type: typeof SET_COMMUNITY_NAME;
  payload: string;
}

export interface setCommunityLogoActionType {
  type: typeof SET_COMMUNITY_LOGO;
  payload: string;
}

export interface setCommunityLogoPublicIdActionType {
  type: typeof SET_COMMUNITY_LOGO_PUBLIC_ID;
  payload: string;
}

export interface setPrimaryColorActionType {
  type: typeof SET_PRIMARY_COLOR;
  payload: string;
}

export interface setFacebookLoginEnabledActionType {
  type: typeof SET_FACEBOOK_LOGIN_ENABLED;
  payload: boolean;
}

export interface setGoogleLoginEnabledActionType {
  type: typeof SET_GOOGLE_LOGIN_ENABLED;
  payload: boolean;
}

export interface setGithubLoginEnabledActionType {
  type: typeof SET_GITHUB_LOGIN_ENABLED;
  payload: boolean;
}

export interface setMemberFeatureFlagsActionType {
  type: typeof SET_MEMBER_FEATURE_FLAGS;
  payload: MemberFeatureFlags;
}

export interface setSSTFeatureFlagsActionType {
  type: typeof SET_SST_FEATURE_FLAGS;
  payload: SSTFeatureFlags;
}

export interface setAuthFeatureFlagsActionType {
  type: typeof SET_AUTH_FEATURE_FLAGS;
  payload: AuthFeatureFlags;
}

export interface setSkipLogin2FAAllowedActionType {
  type: typeof SET_SKIP_LOGIN_2FA_ALLOWED;
  payload: boolean;
}

export interface setForumsAudienceActionType {
  type: typeof SET_FORUMS_AUDIENCE;
  payload: string[];
}

export interface setForumsLimitsActionType {
  type: typeof SET_FORUMS_LIMITS;
  payload: { forumsCommentMaxLength: number; forumsCommentEditWindowHours: number };
}

export type SettingsActionTypes =
  | setCommunityNameActionType
  | setCommunityLogoActionType
  | setPrimaryColorActionType
  | setCommunityLogoPublicIdActionType
  | setFacebookLoginEnabledActionType
  | setGoogleLoginEnabledActionType
  | setGithubLoginEnabledActionType
  | setMemberFeatureFlagsActionType
  | setSSTFeatureFlagsActionType
  | setAuthFeatureFlagsActionType
  | setSkipLogin2FAAllowedActionType
  | setForumsAudienceActionType
  | setForumsLimitsActionType;

// Actions
export const setCommunityName = (name: string): SettingsActionTypes => {
  return {
    type: SET_COMMUNITY_NAME,
    payload: name,
  };
};

export const setCommunityLogo = (logo: string): SettingsActionTypes => {
  return {
    type: SET_COMMUNITY_LOGO,
    payload: logo,
  };
};

export const setCommunityLogoPublicId = (id: string): SettingsActionTypes => {
  return {
    type: SET_COMMUNITY_LOGO_PUBLIC_ID,
    payload: id,
  };
};

export const setPrimaryColor = (color: string): SettingsActionTypes => {
  return {
    type: SET_PRIMARY_COLOR,
    payload: color,
  };
};

export const setFacebookLoginEnabled = (isEnabled: boolean): SettingsActionTypes => {
  return {
    type: SET_FACEBOOK_LOGIN_ENABLED,
    payload: isEnabled,
  };
};

export const setGoogleLoginEnabled = (isEnabled: boolean): SettingsActionTypes => {
  return {
    type: SET_GOOGLE_LOGIN_ENABLED,
    payload: isEnabled,
  };
};

export const setGithubLoginEnabled = (isEnabled: boolean): SettingsActionTypes => {
  return {
    type: SET_GITHUB_LOGIN_ENABLED,
    payload: isEnabled,
  };
};

export const setMemberFeatureFlags = (flags: MemberFeatureFlags): SettingsActionTypes => {
  return {
    type: SET_MEMBER_FEATURE_FLAGS,
    payload: flags,
  };
};

export const setSSTFeatureFlags = (flags: SSTFeatureFlags): SettingsActionTypes => {
  return {
    type: SET_SST_FEATURE_FLAGS,
    payload: flags,
  };
};

export const setAuthFeatureFlags = (flags: AuthFeatureFlags): SettingsActionTypes => {
  return {
    type: SET_AUTH_FEATURE_FLAGS,
    payload: flags,
  };
};

export const setSkipLogin2FAAllowed = (allowed: boolean): SettingsActionTypes => {
  return {
    type: SET_SKIP_LOGIN_2FA_ALLOWED,
    payload: allowed,
  };
};

export const setForumsAudience = (slugs: string[]): SettingsActionTypes => {
  return {
    type: SET_FORUMS_AUDIENCE,
    payload: slugs,
  };
};

export const setForumsLimits = (limits: {
  forumsCommentMaxLength: number;
  forumsCommentEditWindowHours: number;
}): SettingsActionTypes => {
  return {
    type: SET_FORUMS_LIMITS,
    payload: limits,
  };
};

// Reducer
const initialState: Settings = {
  communityName: DefaultCommunity.communityName,
  communityLogo: DefaultCommunity.communityLogo,
  communityLogoPublicId: DefaultCommunity.communityLogoPublicId,
  primaryColor: DefaultCommunity.primaryColor,
  facebookLoginEnabled: DefaultCommunity.facebookLoginEnabled,
  googleLoginEnabled: DefaultCommunity.googleLoginEnabled,
  githubLoginEnabled: DefaultCommunity.githubLoginEnabled,
  memberFeatureFlags: DEFAULT_MEMBER_FEATURE_FLAGS,
  sstFeatureFlags: DEFAULT_SST_FEATURE_FLAGS,
  authFeatureFlags: DEFAULT_AUTH_FEATURE_FLAGS,
  skipLogin2FAAllowed: false,
  forumsAudienceTypeSlugs: [],
  forumsCommentMaxLength: DEFAULT_FORUMS_COMMENT_MAX_LENGTH,
  forumsCommentEditWindowHours: DEFAULT_FORUMS_COMMENT_EDIT_WINDOW_HOURS,
};

export function settingsReducer(state = initialState, action: SettingsActionTypes): Settings {
  switch (action.type) {
    case SET_COMMUNITY_LOGO:
      return {
        ...state,
        communityLogo: action.payload,
      };
    case SET_COMMUNITY_LOGO_PUBLIC_ID:
      return {
        ...state,
        communityLogoPublicId: action.payload,
      };
    case SET_COMMUNITY_NAME:
      return {
        ...state,
        communityName: action.payload,
      };
    case SET_PRIMARY_COLOR:
      return {
        ...state,
        primaryColor: action.payload,
      };
    case SET_FACEBOOK_LOGIN_ENABLED:
      return {
        ...state,
        facebookLoginEnabled: action.payload,
      };
    case SET_GOOGLE_LOGIN_ENABLED:
      return {
        ...state,
        googleLoginEnabled: action.payload,
      };

    case SET_GITHUB_LOGIN_ENABLED:
      return {
        ...state,
        githubLoginEnabled: action.payload,
      };
    case SET_MEMBER_FEATURE_FLAGS:
      return {
        ...state,
        memberFeatureFlags: action.payload,
      };
    case SET_SST_FEATURE_FLAGS:
      return {
        ...state,
        sstFeatureFlags: action.payload,
      };
    case SET_AUTH_FEATURE_FLAGS:
      return {
        ...state,
        authFeatureFlags: action.payload,
      };
    case SET_SKIP_LOGIN_2FA_ALLOWED:
      return {
        ...state,
        skipLogin2FAAllowed: action.payload,
      };
    case SET_FORUMS_AUDIENCE:
      return {
        ...state,
        forumsAudienceTypeSlugs: action.payload,
      };
    case SET_FORUMS_LIMITS:
      return {
        ...state,
        forumsCommentMaxLength: action.payload.forumsCommentMaxLength,
        forumsCommentEditWindowHours: action.payload.forumsCommentEditWindowHours,
      };
    default:
      return state;
  }
}
